import { getMainDomain } from './domain';

// Domínio base do modo PRÓPRIO: o domínio principal do cliente da sessão ativa
// (host do sistema sem o "sistema.").
export const getBrandBaseDomain = () => getMainDomain();

// Wildcard onde toda marca nasce, para o cliente ver o site no mesmo dia em que
// cadastra. Espelha config('brindeme.brands_domain') no backend.
export const getBrandsProvisionalDomain = () => {
    return process.env.REACT_APP_BRANDS_DOMAIN
        || `marcas.${process.env.REACT_APP_BRINDEME_DOMAIN ?? 'brinde.me'}`;
};

// Alvo do CNAME do domínio próprio. Espelha config('brindeme.sistema_host') no
// backend, que confere o apontamento antes de emitir o certificado.
export const BRAND_CNAME_TARGET = process.env.REACT_APP_BRINDEME_SISTEMA_HOST;

// Limites de DNS, não escolha nossa: cada rótulo vai até 63 caracteres e o host
// inteiro até 253. A coluna brands.domain é varchar(255), então quem aperta é o
// DNS.
export const DNS_LABEL_MAX = 63;
export const DNS_HOSTNAME_MAX = 253;

// Quanto o usuário ainda pode digitar no campo de endereço. No provisório ele
// escreve só o rótulo; no próprio, o host inteiro.
export const getUrlValueMaxLength = isCustomDomain => {
    if (isCustomDomain) return DNS_HOSTNAME_MAX;

    // O sufixo do wildcard também ocupa espaço no host final.
    const suffixLength = getBrandsProvisionalDomain().length + 1;

    return Math.min(DNS_LABEL_MAX, DNS_HOSTNAME_MAX - suffixLength);
};

const removeAccents = str => {
    const from = "ãàáäâẽèéëêìíïîõòóöôùúüûñç";
    const to   = "aaaaaeeeeeiiiiooooouuuunc";

    let result = (str ?? '').toString().toLowerCase();
    for (let i = 0; i < from.length; i++) {
        result = result.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }

    return result;
};

// Sugestão de slug a partir do nome da marca.
// Mantém apenas letras e números (sem espaço, traço, ponto ou underscore),
// ex.: "153 Personal Gifts" -> "153personalgifts"
export const generateBrandSlug = name => {
    return removeAccents(name).replace(/[^a-z0-9]/g, '');
};

// Sanitiza o que o usuário digita no campo de subdomínio.
// Subdomínios permitem letras, números e traço (mas não ponto nem underscore).
export const sanitizeSubdomain = value => {
    return removeAccents(value).replace(/[^a-z0-9-]/g, '');
};

// Sanitiza o que o usuário digita no campo de domínio próprio.
// Aceita o formato padrão de domínio (letras, números, ponto e traço),
// permitindo ex.: "cocolapersonalizados.com.br". A sugestão automática gerada
// a partir do nome (generateBrandSlug) continua sem traço, ponto ou underscore.
export const sanitizeDomain = value => {
    return removeAccents(value).replace(/[^a-z0-9.-]/g, '');
};

// No provisório o usuário digita só o rótulo; no próprio, o host inteiro.
export const sanitizeUrlValue = (isCustomDomain, value) => {
    return isCustomDomain ? sanitizeDomain(value) : sanitizeSubdomain(value);
};

// Gera a URL final do site. No provisório o sufixo é o wildcard das marcas; no
// próprio o usuário informa o host completo.
export const buildBrandUrl = (isCustomDomain, value) => {
    if (!value) return '';

    if (isCustomDomain) {
        return `https://${value}`;
    }

    return `https://${value}.${getBrandsProvisionalDomain()}`;
};

const stripSuffix = (host, suffix) => {
    if (!host || !suffix) return host ?? '';

    return host.replace(new RegExp(`\\.?${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i'), '');
};

// Deixa só o rótulo do subdomínio de um host salvo no modo provisório
// (ex.: "minhamarca.marcas.brinde.me" -> "minhamarca").
export const stripBrandsProvisionalDomain = host => stripSuffix(host, getBrandsProvisionalDomain());

// Idem para o domínio principal do cliente
// (ex.: "marca.bestgiftsgroup.com.br" -> "marca").
export const stripBrandBaseDomain = host => stripSuffix(host, getBrandBaseDomain());

// Um host salvo é "próprio" quando não está sob o wildcard das marcas.
export const isCustomBrandHost = host => {
    if (!host) return false;

    return !host.toLowerCase().endsWith(`.${getBrandsProvisionalDomain().toLowerCase()}`);
};

// Resolve a URL de uma imagem da marca. A API pode devolver tanto uma string
// (URL do original) quanto um objeto de tamanhos { original, small, ... }.
export const resolveImageUrl = image => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    return image.original ?? image.medium ?? image.small ?? image.thumbnail ?? image.large ?? null;
};

// Segundos níveis genéricos de ccTLD (com.br, co.uk...). Só serve para separar
// o "Nome" do registro que o cliente cria no provedor; quem valida é o backend.
const GENERIC_SECOND_LEVELS = ['com', 'net', 'org', 'gov', 'edu', 'art', 'adv', 'eng', 'ind', 'inf', 'agr', 'tur', 'blog', 'eco', 'emp', 'app', 'dev', 'log', 'med', 'pro', 'tv', 'co', 'ac'];

// "loja.acme.com.br" -> { name: 'loja', zone: 'acme.com.br', apex: false }.
// Espelha o isApexDomain do backend.
export const splitCustomHost = host => {
    const labels = (host ?? '').toLowerCase().split('.').filter(Boolean);
    const [secondLast, last] = labels.slice(-2);
    const zoneLength = last?.length === 2 && GENERIC_SECOND_LEVELS.includes(secondLast) ? 3 : 2;
    const apex = labels.length <= zoneLength;

    return {
        name: labels.slice(0, labels.length - zoneLength).join('.'),
        zone: labels.slice(-zoneLength).join('.'),
        apex
    };
};

// Espelha o BrandDomainRequest do backend, para avisar enquanto se digita.
export const validateCustomHost = host => {
    if (!host) return null;

    const labels = host.split('.');
    const validLabel = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

    if (labels.some(label => !validLabel.test(label))) {
        return 'Informe um domínio válido, com letras, números, traço e ponto (ex.: loja.suamarca.com.br).';
    }

    if (labels.some(label => label.length > DNS_LABEL_MAX)) {
        return `Cada parte do domínio aceita no máximo ${DNS_LABEL_MAX} caracteres.`;
    }

    if (labels.length < 2 || !/^[a-z]{2,}$/.test(labels[labels.length - 1])) {
        return 'Digite o endereço completo, com o ponto e a extensão (ex.: loja.suamarca.com.br).';
    }

    if (host.startsWith('www.')) {
        return `Endereços com www. não funcionam no site da marca. Use outro subdomínio, como loja.${host.slice(4)}.`;
    }

    if (splitCustomHost(host).apex) {
        return `Use um subdomínio, como loja.${host}. O domínio raiz não aceita o apontamento CNAME na maioria dos provedores.`;
    }

    const baseDomain = process.env.REACT_APP_BRINDEME_DOMAIN ?? 'brinde.me';

    if (host === baseDomain || host.endsWith(`.${baseDomain}`)) {
        return `Endereços em ${baseDomain} não podem ser domínio próprio. Use o endereço provisório da Brinde.me.`;
    }

    return null;
};
